import { useSyncExternalStore } from "react";

/** A tiny observable value, for app-wide UI state that doesn't belong in the query cache. */
export function createStore<T>(initial: T) {
  let value = initial;
  const subs = new Set<() => void>();
  return {
    get: () => value,
    set(next: T | ((prev: T) => T)) {
      value = typeof next === "function" ? (next as (p: T) => T)(value) : next;
      subs.forEach((f) => f());
    },
    subscribe(f: () => void) {
      subs.add(f);
      return () => subs.delete(f);
    },
  };
}

export function useStore<T>(s: ReturnType<typeof createStore<T>>): T {
  return useSyncExternalStore(s.subscribe, s.get, s.get);
}

// ---- clock: one shared 1s tick drives every countdown ----------------------
const clock = createStore(Date.now());
setInterval(() => clock.set(Date.now()), 1000);
document.addEventListener("visibilitychange", () => clock.set(Date.now()));
export const useNow = () => useStore(clock);

// ---- toasts ------------------------------------------------------------------
export interface Toast {
  id: number;
  text: string;
  tone?: "default" | "error";
  action?: { label: string; run: () => void };
}
export const toasts = createStore<Toast[]>([]);
let toastId = 0;

export function toast(text: string, opts: Omit<Toast, "id" | "text"> & { ms?: number } = {}) {
  const id = ++toastId;
  toasts.set((t) => [...t.slice(-2), { id, text, tone: opts.tone, action: opts.action }]);
  setTimeout(() => dismissToast(id), opts.ms ?? (opts.action ? 6000 : 3500));
  return id;
}
export const dismissToast = (id: number) => toasts.set((t) => t.filter((x) => x.id !== id));

// ---- palette -------------------------------------------------------------------
export const palette = createStore<{ open: boolean; initial: string }>({ open: false, initial: "" });
export const openPalette = (initial = "") => palette.set({ open: true, initial });
export const closePalette = () => palette.set({ open: false, initial: "" });

// ---- connection -----------------------------------------------------------------
export const connection = createStore<"live" | "connecting" | "offline">("connecting");

// ---- rows held in place ------------------------------------------------------------
/**
 * Lots whose stance you changed in the current view, and the section they were in. They stay put until
 * you change view or leave. (Bid is the exception: it moves a lot to Priority straight away.)
 */
export const held = createStore<ReadonlyMap<string, "priority" | "time">>(new Map());

// ---- deletes waiting out their Undo window ---------------------------------------
export const deleting = createStore<ReadonlySet<string>>(new Set());
